import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'
import { getDeviceType, ConfigField } from '../config/deviceTypes'
import DeviceDetailPro from './DeviceDetailPro'
import DeviceDetailRadar from './DeviceDetailRadar'

const EVENT_ICONS: Record<string, string> = {
  warning_1: '⚠️',
  warning_2: '🚨',
  warning_3: '🆘',
  ok: '✅',
  motion_alarm: '🏃',
  heartbeat: '💚',
}

const EVENT_TYPE_KEY: Record<string, string> = {
  warning_1: 'warning_1_detail',
  warning_2: 'warning_2_detail',
  warning_3: 'warning_3_detail',
  ok: 'ok_detail',
  motion_alarm: 'motion_alarm',
  heartbeat: 'heartbeat',
}

function timeAgo(dateStr: string, t: Function, lang: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (diff < 60) return t('notificationHistory.justNow')
  if (diff < 3600) {
    const m = Math.floor(diff / 60)
    return t('notificationHistory.minAgo', { min: m })
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600)
    return t('notificationHistory.hAgo', { h })
  }
  return new Date(dateStr).toLocaleString(lang === 'en' ? 'en-GB' : 'sk-SK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function DeviceDetail() {
  const { state } = useLocation()
  const [device, setDevice] = useState<any>(state?.device)

  // PRO zariadenia dostanu vlastne UI — existujuce zariadenia su nedotknute
  if (device?.deviceType === 'motion_v2_zigbee' || device?.deviceType === 'motion_v2_radar_zb') {
    return <DeviceDetailPro device={device} />
  }
  if (device?.deviceType === 'motion_v1_gsm' || device?.deviceType === 'motion_v1_wifi') {
    return <DeviceDetailPro device={device} />
  }
  if (device?.deviceType === 'motion_v2_radar') {
    return <DeviceDetailRadar device={device} />
  }
  const [events, setEvents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [config, setConfig] = useState<Record<string, any>>({})
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error' | 'sent'>('idle')
  const [testSmsSent, setTestSmsSent] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [regenConfirm, setRegenConfirm] = useState(false)
  const [shares, setShares] = useState<any[]>([])
  const [shareEmail, setShareEmail] = useState('')
  const [shareLoading, setShareLoading] = useState(false)
  const [shareError, setShareError] = useState('')
  const [shareSuccess, setShareSuccess] = useState('')
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  const typeDef = getDeviceType(device?.deviceType)

  const initConfig = useCallback((dev: any) => {
    if (dev?.config && Object.keys(dev.config).length > 0) {
      setConfig(dev.config)
    } else if (typeDef) {
      const defaults: Record<string, any> = {}
      typeDef.fields.forEach((f: ConfigField) => { defaults[f.key] = f.defaultValue })
      setConfig(defaults)
    }
  }, [typeDef])

  const loadDevice = useCallback(async () => {
    if (!device?.id) return
    try {
      const res = await api.get(`/devices/${device.id}`)
      setDevice(res.data.device)
      initConfig(res.data.device)
    } catch {}
  }, [device?.id, initConfig])

  const loadEvents = useCallback(async () => {
    if (!device?.id) return
    try {
      const res = await api.get(`/devices/${device.id}/events`)
      setEvents(res.data.events)
    } catch {}
    finally { setLoading(false) }
  }, [device?.id])

  const loadShares = useCallback(async () => {
    if (!device?.id || device?.isShared) return
    try {
      const res = await api.get(`/devices/${device.id}/shares`)
      setShares(res.data.shares)
    } catch {}
  }, [device?.id, device?.isShared])

  useEffect(() => {
    if (!device) { navigate('/dashboard'); return }
    initConfig(device)
    loadDevice()
    loadEvents()
    loadShares()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!window.confirm(t('deviceDetail.deleteConfirm', { name: device.name }))) return
    setDeleting(true)
    try {
      await api.delete(`/devices/${device.id}`)
      navigate('/dashboard')
    } catch {
      alert(t('deviceDetail.deleteError'))
      setDeleting(false)
    }
  }

  const handleRename = async () => {
    if (!newName.trim()) return
    try {
      const res = await api.patch(`/devices/${device.id}/name`, { name: newName.trim() })
      setDevice(res.data.device)
      setRenaming(false)
      setNewName('')
    } catch {}
  }

  const handleRegenKey = async () => {
    if (!window.confirm(t('deviceDetail.regenWarning'))) return
    try {
      const res = await api.post(`/devices/${device.id}/regenerate-key`)
      setDevice(res.data.device)
      setRegenConfirm(true)
      setTimeout(() => setRegenConfirm(false), 5000)
    } catch {}
  }

  const handleSaveConfig = async () => {
    setSaving(true)
    setSaveStatus('idle')
    try {
      // 1. Ulož do DB
      const saved = await api.patch(`/devices/${device.id}/config`, { config })
      setDevice(saved.data.device)

      // 2. Pošli príkazy zariadeniu pre každé pole s commandType
      const commandFields = typeDef?.fields.filter(f => f.commandType && config[f.key] !== undefined) ?? []
      await Promise.all(
        commandFields.map(f =>
          api.post(`/devices/${device.id}/commands`, { type: f.commandType, value: config[f.key] })
        )
      )
      setSaveStatus(device.isOnline ? 'sent' : 'saved')
      setTimeout(() => setSaveStatus('idle'), 4000)
    } catch {
      setSaveStatus('error')
    } finally {
      setSaving(false)
    }
  }

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault()
    setShareLoading(true); setShareError(''); setShareSuccess('')
    try {
      await api.post(`/devices/${device.id}/share`, { email: shareEmail })
      setShareSuccess(t('deviceDetail.shareInviteSent'))
      setShareEmail('')
      loadShares()
    } catch (err: any) {
      setShareError(err.response?.data?.error || t('deviceDetail.shareError'))
    } finally { setShareLoading(false) }
  }

  const handleRemoveShare = async (shareId: string) => {
    if (!window.confirm(t('deviceDetail.shareConfirmRemove'))) return
    try {
      await api.delete(`/devices/${device.id}/shares/${shareId}`)
      setShares(s => s.filter(x => x.id !== shareId))
    } catch {}
  }

  const handleTestSms = async () => {
    try {
      await api.post(`/devices/${device.id}/commands`, { type: 'test_sms' })
      setTestSmsSent(true)
      setTimeout(() => setTestSmsSent(false), 3000)
    } catch {}
  }

  const renderConfigField = (field: ConfigField) => {
    const label = i18n.language === 'en' ? field.labelEn : field.labelSk
    const value = config[field.key] ?? field.defaultValue

    if (field.type === 'mode_select') {
      const modes = [
        { value: 'Absencia pohybu', icon: '🏠', label: t('deviceDetail.modes.absence') },
        { value: 'Alarm pri pohybe', icon: '🚨', label: t('deviceDetail.modes.alarm') },
        { value: 'Vypnuté', icon: '😴', label: t('deviceDetail.modes.off') },
      ]
      return (
        <div key={field.key} style={styles.configSection}>
          <span style={styles.configLabel}>{label}</span>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {modes.map(m => (
              <button
                key={m.value}
                onClick={async () => {
                  const newConfig = { ...config, [field.key]: m.value }
                  setConfig(newConfig)
                  try {
                    const saved = await api.patch(`/devices/${device.id}/config`, { config: newConfig })
                    setDevice(saved.data.device)
                    if (field.commandType) {
                      await api.post(`/devices/${device.id}/commands`, { type: field.commandType, value: m.value }).catch(() => {})
                    }
                  } catch {}
                }}
                style={{
                  flex: 1, padding: '12px 4px', borderRadius: 12,
                  border: value === m.value ? '2px solid #2D7A3A' : '2px solid #f0f0f0',
                  backgroundColor: value === m.value ? '#e8f5f0' : '#fafafa',
                  color: value === m.value ? '#2D7A3A' : '#888',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4
                }}
              >
                <span style={{ fontSize: 22 }}>{m.icon}</span>
                {m.label}
              </button>
            ))}
          </div>
        </div>
      )
    }

    if (field.type === 'phone') {
      return (
        <div key={field.key} style={styles.configRow}>
          <span style={styles.configLabel}>{label}</span>
          <input
            type="tel"
            value={value}
            onChange={e => setConfig({ ...config, [field.key]: e.target.value })}
            style={{ width: 160, padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 14, outline: 'none' }}
          />
        </div>
      )
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.key} style={{ padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
          <span style={{ ...styles.configLabel, display: 'block', marginBottom: 8 }}>{label}</span>
          <textarea
            value={value}
            onChange={e => setConfig({ ...config, [field.key]: e.target.value })}
            rows={3}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
        </div>
      )
    }

    if (field.type === 'boolean') {
      return (
        <div key={field.key} style={styles.configRow}>
          <span style={styles.configLabel}>{label}</span>
          <label style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={!!value} onChange={e => setConfig({ ...config, [field.key]: e.target.checked })} style={{ display: 'none' }} />
            <div style={{ width: 44, height: 24, borderRadius: 12, backgroundColor: value ? '#2D7A3A' : '#ddd', position: 'relative', transition: 'background 0.2s' }}>
              <div style={{ position: 'absolute', top: 3, left: value ? 23 : 3, width: 18, height: 18, borderRadius: 9, backgroundColor: '#fff', transition: 'left 0.2s' }} />
            </div>
          </label>
        </div>
      )
    }

    if (field.type === 'select' && field.options) {
      return (
        <div key={field.key} style={styles.configRow}>
          <span style={styles.configLabel}>{label}</span>
          <select value={value} onChange={e => setConfig({ ...config, [field.key]: e.target.value })}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 14, outline: 'none', backgroundColor: '#fff' }}>
            {field.options.map(opt => (
              <option key={opt.value} value={opt.value}>{i18n.language === 'en' ? opt.labelEn : opt.labelSk}</option>
            ))}
          </select>
        </div>
      )
    }

    if (field.type === 'number') {
      return (
        <div key={field.key} style={styles.configRow}>
          <span style={styles.configLabel}>{label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="number" value={value} min={field.min} max={field.max}
              onChange={e => setConfig({ ...config, [field.key]: Number(e.target.value) })}
              style={{ width: 70, padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 14, outline: 'none', textAlign: 'center' }}
            />
            {field.unit && <span style={{ fontSize: 13, color: '#888' }}>{field.unit}</span>}
          </div>
        </div>
      )
    }

    return null
  }

  const saveButtonLabel = () => {
    if (saving) return t('deviceDetail.savingConfig')
    if (saveStatus === 'sent') return t('deviceDetail.configSent')
    if (saveStatus === 'saved') return t('deviceDetail.configSaved')
    if (saveStatus === 'error') return t('deviceDetail.configError')
    return t('deviceDetail.saveConfig')
  }

  const isOnline = device?.isOnline
  const lastMotion = device?.config?.lastMotion
  const currentMode = device?.config?.mode
  const rssi = device?.config?.rssi
  const battery = device?.config?.battery

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button style={styles.back} onClick={() => navigate('/dashboard')}>← {t('deviceDetail.back')}</button>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: 14, cursor: 'pointer', padding: 0 }} onClick={handleDelete} disabled={deleting}>
              {deleting ? t('deviceDetail.deleting') : t('deviceDetail.delete')}
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          {typeDef && <span style={{ fontSize: 28 }}>{typeDef.icon}</span>}
          <h1 style={styles.title}>{device?.name}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ ...styles.badge, backgroundColor: isOnline ? '#e8f5f0' : '#f5f5f5', color: isOnline ? '#2D7A3A' : '#888' }}>
            {isOnline ? '● Online' : '○ Offline'}
          </span>
          {device?.deviceType && (
            <span style={{ ...styles.badge, backgroundColor: '#f0f4ff', color: '#4a6cf7' }}>
              {typeDef ? (i18n.language === 'en' ? typeDef.nameEn : typeDef.nameSk) : device.deviceType}
              {typeDef?.hasGsm && ' 📡'}
            </span>
          )}
          {device?.lastSeen && (
            <span style={{ ...styles.badge, backgroundColor: '#f8f8f8', color: '#aaa' }}>
              {timeAgo(device.lastSeen, t, i18n.language)}
            </span>
          )}
        </div>
      </div>

      <div style={styles.content}>

        {/* Status zo zariadenia */}
        {(lastMotion || currentMode || rssi !== undefined || battery !== undefined) && (
          <div style={styles.card}>
            <p style={styles.cardLabel}>{t('deviceDetail.deviceStatus')}</p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {currentMode && (
                <div style={styles.statusChip}>
                  <span style={{ fontSize: 11, color: '#888' }}>{t('deviceDetail.mode')}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
                    {currentMode === 'Absencia pohybu' ? '🏠 ' : currentMode === 'Alarm pri pohybe' ? '🚨 ' : '😴 '}{currentMode}
                  </span>
                </div>
              )}
              {lastMotion && (
                <div style={styles.statusChip}>
                  <span style={{ fontSize: 11, color: '#888' }}>{t('deviceDetail.lastMotion')}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>🏃 {lastMotion}</span>
                </div>
              )}
              {rssi !== undefined && (
                <div style={styles.statusChip}>
                  <span style={{ fontSize: 11, color: '#888' }}>WiFi RSSI</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: rssi > -70 ? '#2D7A3A' : rssi > -85 ? '#f39c12' : '#e74c3c' }}>
                    📶 {rssi} dBm
                  </span>
                </div>
              )}
              {battery !== undefined && (
                <div style={styles.statusChip}>
                  <span style={{ fontSize: 11, color: '#888' }}>{t('deviceDetail.battery')}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: battery > 50 ? '#2D7A3A' : battery > 20 ? '#f39c12' : '#e74c3c' }}>
                    🔋 {battery}%
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Konfigurácia */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <p style={{ ...styles.cardLabel, margin: 0 }}>{t('deviceConfig.title')}</p>
            <button onClick={() => { loadDevice(); loadEvents() }} style={{ background: 'none', border: 'none', color: '#2D7A3A', fontSize: 13, cursor: 'pointer', padding: 0 }}>
              ↻ {t('deviceDetail.refresh')}
            </button>
          </div>
          <div style={{ marginTop: 12 }}>
            {!device?.deviceType ? (
              <p style={{ color: '#aaa', fontSize: 14, margin: 0 }}>{t('deviceConfig.noType')}</p>
            ) : !typeDef ? (
              <p style={{ color: '#aaa', fontSize: 14, margin: 0 }}>{t('deviceConfig.unknownType')}: <code>{device.deviceType}</code></p>
            ) : (
              <>
                {typeDef.fields.map(renderConfigField)}

                {!device?.isShared && <>
                  <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                    <button
                      style={{
                        flex: 1, padding: '12px 20px', borderRadius: 10, border: 'none',
                        backgroundColor: saveStatus === 'error' ? '#e74c3c' : saveStatus !== 'idle' ? '#2D7A3A' : '#1a1a1a',
                        color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', minWidth: 160
                      }}
                      onClick={handleSaveConfig}
                      disabled={saving}
                    >
                      {saveButtonLabel()}
                    </button>

                    {/* Test SMS — len pre motion zariadenia */}
                    {(device.deviceType?.startsWith('motion')) && (
                      <button
                        style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid #e0e0e0', backgroundColor: '#fff', color: '#1a1a1a', fontSize: 14, cursor: 'pointer' }}
                        onClick={handleTestSms}
                        disabled={testSmsSent}
                      >
                        {testSmsSent ? t('deviceDetail.testSmsSent') : '📲 Test SMS'}
                      </button>
                    )}
                  </div>

                  {!isOnline && (
                    <p style={{ fontSize: 12, color: '#aaa', marginTop: 8 }}>
                      {t('deviceDetail.offlineWarning')}
                    </p>
                  )}
                </>}
              </>
            )}
          </div>
        </div>

        {/* Per-device push toggle */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={styles.cardLabel}>{t('deviceDetail.pushNotifications')}</p>
              <p style={{ fontSize: 13, color: '#888', margin: '2px 0 0' }}>
                {t('deviceDetail.pushNotificationsDesc')}
              </p>
            </div>
            <button
              onClick={async () => {
                const newVal = config.pushEnabled === false ? true : false
                const newConfig = { ...config, pushEnabled: newVal }
                setConfig(newConfig)
                await api.patch(`/devices/${device.id}/config`, { config: newConfig })
              }}
              style={{
                width: 48, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                backgroundColor: config.pushEnabled === false ? '#ccc' : '#2D7A3A',
                position: 'relative', transition: 'background-color 0.2s', flexShrink: 0
              }}
            >
              <div style={{
                width: 22, height: 22, borderRadius: 11, backgroundColor: '#fff',
                position: 'absolute', top: 3,
                left: config.pushEnabled === false ? 3 : 23,
                transition: 'left 0.2s'
              }} />
            </button>
          </div>
        </div>

        {/* Activity graph — posledných 7 dní */}
        {events.length > 0 && (() => {
          const days: Record<string, number> = {}
          const now = new Date()
          for (let i = 6; i >= 0; i--) {
            const d = new Date(now); d.setDate(d.getDate() - i)
            days[d.toISOString().slice(0, 10)] = 0
          }
          events.forEach((e: any) => {
            const day = new Date(e.createdAt).toISOString().slice(0, 10)
            if (day in days) days[day]++
          })
          const entries = Object.entries(days)
          const maxVal = Math.max(...entries.map(([,v]) => v), 1)
          return (
            <div style={styles.card}>
              <p style={styles.cardLabel}>{t('deviceDetail.activity7days')}</p>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 64 }}>
                {entries.map(([day, count]) => (
                  <div key={day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: '100%', backgroundColor: count > 0 ? '#2D7A3A' : '#f0f0f0', borderRadius: 4, height: `${Math.max((count / maxVal) * 52, count > 0 ? 6 : 4)}px`, transition: 'height 0.3s' }} />
                    <span style={{ fontSize: 9, color: '#bbb' }}>{new Date(day).getDate()}.{new Date(day).getMonth() + 1}.</span>
                  </div>
                ))}
              </div>
            </div>
          )
        })()}

        {/* História udalostí */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <p style={{ ...styles.cardLabel, margin: 0 }}>{t('deviceDetail.history')}</p>
            {!device?.isShared && (
              <a
                href={`${process.env.REACT_APP_API_URL || 'https://vigilantapp.duckdns.org'}/devices/${device?.id}/events/csv`}
                download
                onClick={e => {
                  e.preventDefault()
                  const token = localStorage.getItem('token')
                  const apiUrl = process.env.REACT_APP_API_URL || 'https://vigilantapp.duckdns.org'
                  fetch(`${apiUrl}/devices/${device?.id}/events/csv`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.blob()).then(blob => {
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a'); a.href = url; a.download = `${device?.name}-events.csv`; a.click()
                      URL.revokeObjectURL(url)
                    })
                }}
                style={{ fontSize: 13, color: '#2D7A3A', textDecoration: 'none', cursor: 'pointer' }}
              >
                ⬇ CSV
              </a>
            )}
          </div>
          {loading ? (
            <p style={{ color: '#888' }}>{t('deviceDetail.loading')}</p>
          ) : events.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '20px 0' }}>{t('deviceDetail.noEvents')}</p>
          ) : (
            events.map((event: any) => {
              const icon = EVENT_ICONS[event.type] ?? '📌'
              const eventKey = EVENT_TYPE_KEY[event.type]
              const label = eventKey ? t(`deviceDetail.events.${eventKey}`) : event.type
              return (
                <div key={event.id} style={styles.eventRow}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>{icon}</span>
                    <span style={styles.eventLabel}>{label}</span>
                  </span>
                  <span style={styles.eventTime}>
                    {new Date(event.createdAt).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'sk-SK', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )
            })
          )}
        </div>

        {/* Poznámka k zariadeniu */}
        {!device?.isShared && (
          <div style={styles.card}>
            <p style={styles.cardLabel}>{t('deviceDetail.notes')}</p>
            <textarea
              value={config.notes || ''}
              onChange={e => setConfig({ ...config, notes: e.target.value })}
              placeholder={t('deviceDetail.notesPlaceholder')}
              rows={3}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #e0e0e0', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', color: '#1a1a1a' }}
              onBlur={async () => {
                try { await api.patch(`/devices/${device.id}/config`, { config }) } catch {}
              }}
            />
            <p style={{ fontSize: 12, color: '#bbb', margin: '6px 0 0' }}>{t('deviceDetail.notesSaved')}</p>
          </div>
        )}

        {/* Rename device */}
        <div style={styles.card}>
          <p style={styles.cardLabel}>{t('deviceDetail.rename')}</p>
          {renaming ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid #e0e0e0', fontSize: 15, outline: 'none' }}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder={device?.name}
                autoFocus
              />
              <button onClick={handleRename} style={{ padding: '10px 16px', borderRadius: 10, border: 'none', backgroundColor: '#2D7A3A', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {t('deviceDetail.renameSave')}
              </button>
              <button onClick={() => { setRenaming(false); setNewName('') }} style={{ padding: '10px 14px', borderRadius: 10, border: 'none', backgroundColor: '#f0f0f0', color: '#666', fontSize: 14, cursor: 'pointer' }}>
                ✕
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', margin: 0 }}>{device?.name}</p>
              <button onClick={() => { setRenaming(true); setNewName(device?.name || '') }} style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 14px', fontSize: 13, color: '#555', cursor: 'pointer' }}>
                ✏️ {t('deviceDetail.renameBtn')}
              </button>
            </div>
          )}
        </div>

        {/* Zdieľaný prístup — len pre vlastníka */}
        {!device?.isShared && (
          <div style={styles.card}>
            <p style={styles.cardLabel}>{t('deviceDetail.sharedAccess')}</p>
            <p style={{ fontSize: 13, color: '#888', margin: '0 0 12px' }}>
              {t('deviceDetail.sharedAccessDesc')}
            </p>
            {shareSuccess && <p style={{ fontSize: 13, color: '#2D7A3A', margin: '0 0 10px', fontWeight: 600 }}>✅ {shareSuccess}</p>}
            {shareError && <p style={{ fontSize: 13, color: '#e74c3c', margin: '0 0 10px' }}>{shareError}</p>}
            <form onSubmit={handleShare} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="email"
                placeholder={t('deviceDetail.shareEmailPlaceholder')}
                value={shareEmail}
                onChange={e => setShareEmail(e.target.value)}
                required
                style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid #e0e0e0', fontSize: 14, outline: 'none' }}
              />
              <button type="submit" disabled={shareLoading} style={{ padding: '10px 16px', borderRadius: 10, border: 'none', backgroundColor: '#2D7A3A', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {shareLoading ? '...' : t('deviceDetail.shareInvite')}
              </button>
            </form>
            {shares.length > 0 && (
              <div>
                {shares.map((s: any) => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid #f5f5f5' }}>
                    <div>
                      <span style={{ fontSize: 14, color: '#1a1a1a' }}>{s.sharedWithEmail}</span>
                      <span style={{ fontSize: 12, color: s.accepted ? '#2D7A3A' : '#f39c12', marginLeft: 8 }}>
                        {s.accepted ? t('deviceDetail.shareActive') : t('deviceDetail.sharePending')}
                      </span>
                    </div>
                    <button onClick={() => handleRemoveShare(s.id)} style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: 13, cursor: 'pointer', padding: '4px 8px' }}>
                      {t('deviceDetail.shareRemove')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Zdieľané zariadenie — info badge */}
        {device?.isShared && (
          <div style={{ ...styles.card, backgroundColor: '#f0fdf4' }}>
            <p style={{ fontSize: 14, color: '#2D7A3A', margin: 0, fontWeight: 500 }}>
              👥 {t('deviceDetail.sharedBadge')}
            </p>
          </div>
        )}

        {/* Device Key */}
        <div style={{ ...styles.card, backgroundColor: '#fafafa' }}>
          <p style={styles.cardLabel}>Device Key</p>
          <p style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a', margin: 0, fontFamily: 'monospace', letterSpacing: 1 }}>
            {device?.deviceKey}
          </p>
          <p style={{ fontSize: 12, color: '#aaa', margin: '6px 0 0' }}>
            {t('deviceDetail.deviceKeyDesc')}
          </p>
          {regenConfirm && <p style={{ fontSize: 13, color: '#2D7A3A', margin: '8px 0 0', fontWeight: 600 }}>✅ {t('deviceDetail.regenConfirm')}</p>}
          <button onClick={handleRegenKey} style={{ marginTop: 10, background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 14px', fontSize: 13, color: '#e74c3c', cursor: 'pointer' }}>
            🔄 {t('deviceDetail.regenKey')}
          </button>
        </div>

      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#f8f9fa' },
  header: { backgroundColor: '#fff', padding: '24px 20px', borderBottom: '1px solid #f0f0f0', maxWidth: 600, margin: '0 auto' },
  back: { background: 'none', border: 'none', color: '#2D7A3A', fontSize: 17, cursor: 'pointer', padding: 0, marginBottom: 8, display: 'block' },
  title: { fontSize: 24, fontWeight: 600, color: '#1a1a1a', margin: 0 },
  badge: { padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500 },
  content: { maxWidth: 600, margin: '0 auto', padding: 20 },
  card: { backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' },
  cardLabel: { fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' },
  configSection: { padding: '10px 0', borderBottom: '1px solid #f5f5f5' },
  configRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5' },
  configLabel: { fontSize: 15, color: '#1a1a1a' },
  statusChip: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 120 },
  eventRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f5f5f5' },
  eventLabel: { fontSize: 15, color: '#1a1a1a' },
  eventTime: { fontSize: 13, color: '#888', flexShrink: 0, marginLeft: 8 },
}
